import React, { useCallback, useEffect } from "react";

import { getSessionDetails } from "../../utils/userSession";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useReactiveState } from "../../utils/stateManagement";
import {
    UserSessionListState,
    deleteSelectedSessions,
    fetchData,
    UserSessionListDialog,
    RenderUserSessionList,
    labelTranslations,
} from "./common";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import { useFetch } from "../FetchProvider";
import { tokenResourceCreatedEventName } from "../../constants/Constants";
import { Table, TableCell, TableHead, TableRow } from "@mui/material";

const UserSessionTable: React.FC<{ userSub: string }> = ({ userSub }) => {
    const state = useReactiveState<UserSessionListState>({
        rows: [],
        sessionDialogData: null,
        showDialog: false,
    });
    const fetch = useFetch();

    const { setRows } = state;
    const _fetchData = useCallback(() => fetchData(userSub, fetch).then((rows) => setRows(rows)), [fetch, setRows, userSub]);

    useEffect(() => {
        _fetchData().then();

        document.addEventListener(tokenResourceCreatedEventName, _fetchData);

        return () => {
            document.removeEventListener(tokenResourceCreatedEventName, _fetchData);
        };
    }, [_fetchData]);

    const Label = ({ message }: { message: MessageDescriptor }) => (
        <span>
            {/* <FormattedMessage id={`user_session.${name}`} /> */}
            <FormattedMessage {...message} />
        </span>
    );

    const unknown = <Label message={labelTranslations.unknown} />;

    const _deleteSelectedSessions = async () => {
        await deleteSelectedSessions([state.sessionDialogData["id"]], fetch);
        state.setShowDialog(false);
        await _fetchData();
    };

    const _showSessionDialog = (data: any) => {
        state.setSessionDialogData(data);
        state.setShowDialog(true);
    };

    const Header = () => (
        <TableHead>
            <TableRow>
                <TableCell>
                    <Label message={labelTranslations.type} />
                </TableCell>
                <TableCell>
                    <Label message={labelTranslations.device} />
                </TableCell>
                <TableCell>
                    <Label message={labelTranslations.os} />
                </TableCell>
                <TableCell>
                    <Label message={labelTranslations.client} />
                </TableCell>
                <TableCell>
                    <Label message={labelTranslations.last_use} />
                </TableCell>
                <TableCell>
                    <Label message={labelTranslations.apps} />
                </TableCell>
            </TableRow>
        </TableHead>
    );

    const Row = (session: any) => {
        const { lastUse } = getSessionDetails(session);
        const userAgent = session["user_agent"];
        let lastUseComp = <span>{lastUse}</span>;

        if (session["current"])
            lastUseComp = (
                <span>
                    <FontAwesomeIcon icon={faCheckCircle} color="#0a0" style={{ marginRight: 4 }} />
                    <Label message={labelTranslations.active} />
                </span>
            );

        return (
            <TableRow onClick={() => _showSessionDialog(session)}>
                <TableCell>{session["type"]}</TableCell>
                <TableCell>{userAgent ? userAgent["device"] : unknown}</TableCell>
                <TableCell>{userAgent ? userAgent["os"]["name"] : unknown}</TableCell>
                <TableCell>{userAgent ? userAgent["client"]["name"] : unknown}</TableCell>
                <TableCell>{lastUseComp}</TableCell>
                <TableCell>{session["apps"].map((app: any) => app["name"]).join(", ")}</TableCell>
            </TableRow>
        );
    };

    const Dialog = () => (
        <UserSessionListDialog
            open={state.showDialog}
            data={state.sessionDialogData}
            hide={() => state.setShowDialog(false)}
            deleteSelectedSessions={_deleteSelectedSessions}
            disableDeleteCurrentSession={false}
        />
    );

    const List = () => {
        return (
            <Table>
                <Header />
                <tbody>{...state.rows.map((s) => Row(s))}</tbody>
            </Table>
        );
    };

    return <RenderUserSessionList Dialog={Dialog} List={List} rowsLength={state.rows.length} userSub={userSub} />;
};

export default UserSessionTable;
